/**
 * pw-track — unified analytics helper for Paragraph.
 *
 * Goal: 100% coverage of conversion-relevant events across every signup
 * surface (geo-banner inline form, newsletter footer, PDP notify-me, etc.)
 * so Google Ads / GA4 / GTM all see the same data, and Enhanced
 * Conversions can match users across devices via the email.
 *
 * Public API:
 *   PW.track.emailSignup({ surface, email, country })
 *     surface  — short identifier ('geo_banner', 'newsletter_footer',
 *                'pdp_notify_me', 'email_signup_banner')
 *     email    — plaintext (Google's tag hashes it client-side for EC)
 *     country  — ISO-3166 alpha-2 (best effort; helps segment)
 *
 *   PW.track.partnerRedirect({ partner, url, country })
 *     Fired when a visitor commits to a third-party retailer handoff
 *     (e.g. Global Wine on the CH market).
 *
 * The helper is no-op-safe: if neither gtag nor dataLayer is loaded
 * (e.g. before GTM/GA4 is wired up), calls just queue to a tiny
 * in-memory buffer and replay once the analytics globals appear. This
 * means we can deploy the conversion-tracking code BEFORE the GA4 /
 * Google Ads / GTM tags are installed, and no events get lost.
 *
 * Privacy: emails are passed RAW to Google's gtag; Google's tag hashes
 * them with SHA-256 before sending to its servers (the standard
 * Enhanced Conversions flow). We never store the email locally beyond
 * the in-flight call. GDPR consent must be obtained before invoking
 * emailSignup() — that's the caller's responsibility (forms in this
 * codebase already require an explicit consent checkbox before submit).
 */
(function () {
  'use strict';

  var BUFFER_KEY = '__pw_track_buffer';
  var MAX_BUFFER = 32;

  function ensureBuffer() {
    if (!Array.isArray(window[BUFFER_KEY])) window[BUFFER_KEY] = [];
    return window[BUFFER_KEY];
  }

  function hasGtag() { return typeof window.gtag === 'function'; }
  function hasDataLayer() { return Array.isArray(window.dataLayer); }

  function flushBuffer() {
    var buf = ensureBuffer();
    if (!buf.length) return;
    if (!hasGtag() && !hasDataLayer()) return;
    var pending = buf.splice(0, buf.length);
    pending.forEach(function (entry) {
      try { entry(); } catch (e) { /* swallow */ }
    });
  }

  function pushOrBuffer(fire) {
    if (hasGtag() || hasDataLayer()) {
      try { fire(); } catch (e) { /* swallow */ }
    } else {
      var buf = ensureBuffer();
      if (buf.length < MAX_BUFFER) buf.push(fire);
    }
  }

  // Watch for late-loading GA4/GTM and replay the buffer when ready.
  // Re-checks every 500ms for the first 30s of the page, then quiets.
  var pollAttempts = 0;
  var pollHandle = setInterval(function () {
    pollAttempts++;
    if (hasGtag() || hasDataLayer()) {
      flushBuffer();
      clearInterval(pollHandle);
    } else if (pollAttempts > 60) {
      clearInterval(pollHandle);
    }
  }, 500);

  // Klaviyo list subscribe — adds the signup directly to a Klaviyo list with
  // marketing consent, country + source. This is the server-of-record for
  // the email program; it is INDEPENDENT of GA4/GTM (which only mirror the
  // signup for ad attribution). No-op unless window.PW.config.klaviyoListId
  // is set, so it's safe to ship on stores that don't (yet) use Klaviyo.
  //
  // ENDPOINT CHOICE — kmail-lists AJAX, NOT the JSON:API:
  //   Klaviyo's modern /client/subscriptions/ JSON:API endpoint does NOT
  //   send Access-Control-Allow-Origin, so a plain cross-origin fetch from
  //   the storefront is blocked by CORS (verified live: "blocked by CORS
  //   policy, No 'Access-Control-Allow-Origin'"). no-cors silently drops the
  //   body too. The supported client-side path is the form-encoded AJAX
  //   endpoint manage.kmail-lists.com/ajax/subscriptions/subscribe, which is
  //   CORS-enabled and is exactly what Klaviyo's own onsite forms post to.
  //   It takes the list id as `g` and form fields; consent is governed by
  //   the list's opt-in setting (Va2Kpw = single-opt-in → immediately
  //   SUBSCRIBED, verified end-to-end).
  //
  // Consent: the caller only invokes emailSignup() after the visitor has
  // opted in (the geo banner gates on its own GDPR consent box; the footer/
  // newsletter forms are an explicit subscribe action). So a call here IS
  // the opt-in. The list's opt-in process (single vs double) governs whether
  // a confirmation email is sent; we don't override it.
  //
  // Dedup: a single email may reach here from more than one surface in a
  // page session (e.g. native-form listener + a direct call). Key on the
  // email so we subscribe at most once per email per page load.
  var klaviyoSent = {};

  function klaviyoSubscribe(email, country, surface) {
    if (!email) return;
    var cfg = (window.PW && window.PW.config) || {};
    var listId = cfg.klaviyoListId;
    if (!listId) return; // Klaviyo not configured — no-op.

    var dedupeKey = String(email).trim().toLowerCase();
    if (klaviyoSent[dedupeKey]) return;
    klaviyoSent[dedupeKey] = true;

    // Form-encoded body. `g` is the Klaviyo list id. Custom profile
    // properties are only persisted if their names are declared in the
    // `$fields` parameter (comma-separated) — otherwise the kmail-lists
    // endpoint ignores them. So we declare signup_country / signup_surface
    // there and pass their values as matching fields.
    var body = new URLSearchParams();
    body.append('g', listId);
    body.append('email', email);
    var fields = [];
    if (country) { body.append('signup_country', country); fields.push('signup_country'); }
    if (surface) { body.append('signup_surface', surface); fields.push('signup_surface'); }
    if (fields.length) body.append('$fields', fields.join(','));

    try {
      fetch('https://manage.kmail-lists.com/ajax/subscriptions/subscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8' },
        body: body.toString(),
        keepalive: true // survive the form's full-page navigation
      }).catch(function () { /* swallow — Klaviyo capture is best-effort */ });
    } catch (e) { /* fetch unavailable — swallow */ }
  }

  function emailSignup(params) {
    var surface = (params && params.surface) || 'unknown';
    var email = (params && params.email) || '';
    var country = (params && params.country) || '';

    // Subscribe to Klaviyo first (server-of-record), independent of GA4/GTM
    // buffering — we don't want the email program to wait on analytics load.
    klaviyoSubscribe(email, country, surface);

    pushOrBuffer(function () {
      // GA4 standard sign_up event with Enhanced Conversions payload.
      // Google's gtag SDK hashes user_data.email_address before sending.
      if (hasGtag()) {
        window.gtag('event', 'sign_up', {
          method: 'email',
          surface: surface,
          source_country: country,
          user_data: email ? { email_address: email } : undefined
        });
      }
      // GTM dataLayer push — same event under a clearer name so GTM
      // triggers can fan out to whatever tags the merchant configures
      // (Google Ads conversion, Klaviyo, Meta CAPI, etc.).
      if (hasDataLayer()) {
        window.dataLayer.push({
          event: 'email_signup',
          surface: surface,
          source_country: country,
          // Email is also passed via user_data for GTM templates that
          // map it through to Google Ads enhanced conversions.
          user_data: email ? { email_address: email } : undefined
        });
      }
    });
  }

  // ---- First-touch attribution capture (cross-store hand-off) ----------
  //
  // Problem: a visitor who lands on one store from an ad (utm_* / gclid /
  // fbclid in the landing URL), browses a page or two, then switches to the
  // sibling store loses all campaign attribution — the query string is gone
  // after the first internal navigation, so the destination store only sees
  // the switcher's generic cross_store stamp.
  //
  // Fix: persist the attribution-relevant params from the FIRST page of the
  // session (first touch wins) in sessionStorage, and let the store selector
  // replay them onto the cross-store URL (see appendCrossStoreUtms in
  // paragraph-store-selector.js). Session-scoped on purpose: attribution
  // hand-off is a within-session concern, and session storage keeps the
  // ePrivacy footprint minimal.
  //
  // Consent: utm_* values are campaign metadata, not personal data — they are
  // captured and replayed unconditionally. Click IDs (gclid/fbclid/...) are
  // pseudonymous identifiers, so they are captured (they're already in the
  // URL we're on) but only RELEASED for replay when marketing consent is
  // granted. Gating at read time rather than capture time matters because
  // on EU_OPT_IN the consent banner is answered AFTER the landing page load —
  // gating capture would mean click IDs never survive at all.
  var FT_KEY = 'pw:first-touch';
  var FT_UTMS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'utm_id'];
  var FT_CLICK_IDS = ['gclid', 'gbraid', 'wbraid', 'fbclid', 'msclkid', 'ttclid'];

  // Marketing-consent check against the LBMGB consent framework
  // (lbmgb-consent-banner.js). Reads the stored choice directly; when no
  // choice is recorded yet, falls back to the region-zone default the banner
  // itself uses on boot: EU_OPT_IN = deny-until-answered, US_OPT_OUT / ROW =
  // granted by default.
  function marketingConsentAllowed() {
    try {
      var raw = window.localStorage.getItem('lbmgb_consent_v1');
      if (raw) {
        var v = JSON.parse(raw);
        if (v && typeof v.marketing === 'boolean') return v.marketing;
      }
    } catch (e) { /* storage blocked / malformed — fall through to zone default */ }
    var zone = (window.LBMGB && window.LBMGB.config && window.LBMGB.config.regionZone) || 'ROW';
    return zone !== 'EU_OPT_IN';
  }

  function readFirstTouch() {
    try {
      var v = JSON.parse(window.sessionStorage.getItem(FT_KEY));
      return (v && v.v === 1) ? v : null;
    } catch (e) { return null; }
  }

  function captureFirstTouch() {
    try {
      if (readFirstTouch()) return; // first touch wins
      var qs = new URLSearchParams(window.location.search);
      var utm = {};
      var ids = {};
      var any = false;
      FT_UTMS.forEach(function (k) {
        var val = qs.get(k);
        if (val) { utm[k] = val; any = true; }
      });
      FT_CLICK_IDS.forEach(function (k) {
        var val = qs.get(k);
        if (val) { ids[k] = val; any = true; }
      });
      if (!any) return; // nothing attribution-relevant on this URL
      window.sessionStorage.setItem(FT_KEY, JSON.stringify({
        v: 1,
        utm: utm,
        ids: ids,
        // Stored for diagnostics / future server-side use; not emitted
        // into any outgoing URL.
        ref: document.referrer || '',
        lp: window.location.pathname
      }));
    } catch (e) { /* URLSearchParams or storage unavailable — degrade silently */ }
  }

  // Flat replay object for the cross-store hand-off, e.g.
  //   { utm_source: 'google', utm_medium: 'cpc', gclid: '…' }
  // Click IDs are included only when marketing consent is granted (see the
  // module comment above). Returns null when there's nothing to replay.
  function getFirstTouch() {
    var ft = readFirstTouch();
    if (!ft) return null;
    var out = {};
    var has = false;
    var k;
    for (k in ft.utm) {
      if (Object.prototype.hasOwnProperty.call(ft.utm, k)) { out[k] = ft.utm[k]; has = true; }
    }
    if (marketingConsentAllowed()) {
      for (k in ft.ids) {
        if (Object.prototype.hasOwnProperty.call(ft.ids, k)) { out[k] = ft.ids[k]; has = true; }
      }
    }
    return has ? out : null;
  }

  captureFirstTouch();

  // ---- Cross-store arrival reporting ------------------------------------
  //
  // The store switcher on the SOURCE store decorates the hand-off URL with
  // either a `pw_xs=<from>` breadcrumb (real campaign attribution carried
  // across) or the fallback utm_source=paragraph_<from>&utm_medium=cross_store
  // stamp (no known first touch). This fires a single `cross_store_arrival`
  // event on the DESTINATION store so switcher-driven sessions are countable
  // in one place regardless of which form the hand-off took:
  //   pw_xs      — source store code ('eu', 'us', ...), registered as an
  //                event-scoped custom dimension in both GA4 properties
  //   attributed — 'true' when original campaign params were carried across,
  //                'false' when the visit fell back to the cross_store stamp
  // Once per session (sessionStorage guard) so reloads of the arrival URL
  // don't inflate the count.
  var XS_FIRED_KEY = 'pw:xs-arrival-fired';

  function reportCrossStoreArrival() {
    try {
      if (window.sessionStorage.getItem(XS_FIRED_KEY)) return;
      var qs = new URLSearchParams(window.location.search);
      var from = qs.get('pw_xs');
      var attributed = true;
      if (!from) {
        // Fallback-stamp form: utm_source=paragraph_<from>&utm_medium=cross_store
        var src = qs.get('utm_source') || '';
        if (qs.get('utm_medium') === 'cross_store' && src.indexOf('paragraph_') === 0) {
          from = src.slice('paragraph_'.length);
          attributed = false;
        }
      }
      if (!from) return;
      window.sessionStorage.setItem(XS_FIRED_KEY, '1');

      pushOrBuffer(function () {
        if (hasGtag()) {
          window.gtag('event', 'cross_store_arrival', {
            pw_xs: from,
            attributed: attributed ? 'true' : 'false'
          });
        }
        if (hasDataLayer()) {
          window.dataLayer.push({
            event: 'cross_store_arrival',
            pw_xs: from,
            attributed: attributed ? 'true' : 'false'
          });
        }
      });
    } catch (e) { /* storage / URLSearchParams unavailable — degrade silently */ }
  }

  reportCrossStoreArrival();

  function partnerRedirect(params) {
    var partner = (params && params.partner) || 'unknown';
    var url = (params && params.url) || '';
    var country = (params && params.country) || '';

    pushOrBuffer(function () {
      if (hasGtag()) {
        window.gtag('event', 'partner_redirect', {
          partner_name: partner,
          destination_url: url,
          source_country: country
        });
      }
      if (hasDataLayer()) {
        window.dataLayer.push({
          event: 'partner_redirect',
          partner_name: partner,
          destination_url: url,
          source_country: country
        });
      }
    });
  }

  // Auto-attach to every Shopify customer-form (newsletter footer,
  // email-signup-banner, PDP notify-me, etc.). On submit we fire the
  // emailSignup event BEFORE letting the form post natively. We don't
  // wait for server confirmation because Shopify's newsletter form
  // does a full-page navigation back to the same URL with
  // ?customer_posted=true; tagging on submit-intent gives us the data
  // even though the user technically hasn't been confirmed yet.
  // Server-side confirmation (Shopify webhook → GTM) is the correct
  // long-term path, but for client-side this captures the intent.
  //
  // Surface is inferred from the form's location:
  //   - .newsletter-form inside email-signup-banner section → "email_signup_banner"
  //   - .newsletter-form inside footer → "newsletter_footer"
  //   - .newsletter-form inside PDP / pw-notify-me snippet → "pdp_notify_me"
  //   - .newsletter-form anywhere else → "newsletter_generic"
  // Bypassed for the geo-banner inline form which calls
  // PW.track.emailSignup directly via JS (more accurate timing).
  function inferSurface(form) {
    if (form.closest('[data-banner-signup]')) return null; // handled by selector JS
    if (form.closest('.email-signup-banner')) return 'email_signup_banner';
    if (form.closest('footer, .footer, [class*="footer"]')) return 'newsletter_footer';
    if (form.closest('.pw-notify-me, [data-pw-notify-me]') || form.id === 'pw-notify-me-form') return 'pdp_notify_me';
    return 'newsletter_generic';
  }

  function attachNewsletterListener() {
    document.addEventListener('submit', function (e) {
      var form = e.target;
      if (!form || form.tagName !== 'FORM') return;
      // Shopify customer-form forms have name="contact[email]". This
      // is a stable signal across all newsletter / signup variants.
      var emailEl = form.querySelector('input[name="contact[email]"], input[type="email"][name*="contact"]');
      if (!emailEl) return;
      var surface = inferSurface(form);
      if (!surface) return; // explicitly handled elsewhere
      var country = (document.documentElement.getAttribute('data-current-country') || '').toUpperCase();
      emailSignup({ surface: surface, email: emailEl.value, country: country });
    }, true /* capture phase so we fire before any form-validation handlers cancel */);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', attachNewsletterListener);
  } else {
    attachNewsletterListener();
  }

  window.PW = window.PW || {};
  window.PW.track = {
    emailSignup: emailSignup,
    partnerRedirect: partnerRedirect,
    getFirstTouch: getFirstTouch,
    _flush: flushBuffer
  };
})();
